import { FC, ReactNode } from "react";
import { getDataAutomationGroup, getDataLinkAttributes } from "@/helpers";
import { Theme } from "@/types";
import { Link as LinkProps } from "@/types/General/Link";
import Link from "@/components/Link";
import GlobalFooterRedesigned from "./GlobalFooterRedesigned";

export type GlobalFooterProps = {
  isRedesigned?: boolean;
  logo?: ReactNode;
  description?: ReactNode;
  links: Array<{
    link_category?: string;
    link: Array<LinkProps>;
  }>;
  theme?: Theme;
  socialsText?: ReactNode;
  socials?: Array<{
    link: LinkProps;
    icon?: ReactNode;
  }>;
  cta?: ReactNode;
};

export const GlobalFooter: FC<GlobalFooterProps> = ({
  isRedesigned = false,
  logo,
  description,
  socials,
  socialsText,
  links,
  theme,
  cta,
}) => {
  return isRedesigned ? (
    <GlobalFooterRedesigned
      logo={logo}
      description={description}
      socials={socials}
      socialsText={socialsText}
      links={links}
      theme={theme}
      cta={cta}
    />
  ) : (
    <footer
      data-theme={theme}
      className="bg-background mt-auto overflow-x-hidden"
    >
      <div className="relative">
        <div className="swdc-container lg:pt-14">
          <div className="flex flex-col lg:flex-row lg:gap-x-30">
            <div className="my-12 lg:my-0 lg:max-w-lg">
              <div className="max-h-14 max-w-50 sm:max-w-60 lg:max-h-12">
                {logo}
              </div>
              <div className="mt-6">{description}</div>
            </div>
            <div className="grid w-full grid-cols-2 gap-x-8 pb-12 sm:grid-flow-col sm:grid-cols-3 sm:grid-rows-2 sm:pb-0 md:gap-x-14 lg:max-w-152 lg:gap-x-4">
              {links.map(({ link_category, link }, index) => (
                <div
                  key={`${link_category ?? "category"}-${index}`}
                  className="text-headline mb-8 flex break-inside-avoid flex-col lg:min-w-48"
                >
                  {link_category && (
                    <span className="mb-3 font-bold">{link_category}</span>
                  )}
                  <ul>
                    {link.map(({ title, href, dlt, dld }, linkIndex) => (
                      <li
                        key={`${index}-${linkIndex}-${title}`}
                        className="mb-3 leading-0 last:mb-0"
                      >
                        <Link
                          className="active:text-tab-link-pressed h-fit leading-5 transition-all duration-300 hover:underline active:underline"
                          href={href}
                          {...getDataLinkAttributes({
                            dlt: dlt || "Footer",
                            dld: dld || `${title} link`,
                          })}
                        >
                          {title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="col-span-1 row-start-4 w-fit sm:row-start-[unset] sm:mt-0 sm:w-full">
                <ul className="mb-8 flex items-center sm:mb-10 lg:w-full">
                  {socials &&
                    socials.length > 0 &&
                    socials.map(({ link, icon }, index) => (
                      <li
                        className="mr-2 flex size-8 max-w-8 cursor-pointer items-center justify-center last:mr-0"
                        key={`${link.title}-${index}`}
                        {...getDataAutomationGroup("social-icon")}
                      >
                        <Link
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...getDataLinkAttributes({
                            dlt: link.dlt || "Footer",
                            dld: link.dld || `${link.title} social icon`,
                          })}
                          aria-label={link.title}
                          {...getDataAutomationGroup("link")}
                        >
                          {icon}
                        </Link>
                      </li>
                    ))}
                </ul>
                <div className="lg:mt-10 lg:ml-auto">{cta}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default GlobalFooter;
